'use client';

import { useEffect, useState } from 'react';

import type { TSubscriptionItem } from '~entities/subscription';
import { annals } from '~features/annals/hooks';
import { useLanguage } from '~features/language';
import { Toaster } from '~features/toaster/Toaster';
import {
	DatadogLogger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';

import { ConfirmModal } from './ui/ConfirmModal';
import { Feedback } from './ui/Feedback';
import { MainModal } from './ui/MainModal';
import { PromptModal } from './ui/PromptModal';
import { ReasonsModal } from './ui/ReasonsModal';
import { SuccessModal } from './ui/SuccessModal';

type Props = {
	onClose: () => void;
	activeSubscription: TSubscriptionItem | undefined;
};

const isCanceled = (subscription: TSubscriptionItem | undefined) =>
	subscription?.cancelDate;

type Screen =
	| 'main'
	| 'feedback'
	| 'reasons'
	| 'confirm'
	| 'prompt'
	| 'canceled';

export const MySubscriptions = ({ onClose, activeSubscription }: Props) => {
	const trackMainModalOpen = annals.subscription.useViewModalPremiumActive();
	const [screen, setScreen] = useState<Screen>(
		isCanceled(activeSubscription) ? 'canceled' : 'main'
	);
	const [reasons, setReasons] = useState<string>('');
	const { locale } = useLanguage();
	const openMainScreen = () => setScreen('main');

	useEffect(() => {
		if (screen === 'main') {
			trackMainModalOpen();
		}
		// Трекаем только при открытии. Если попали с навигацией, то трекать не надо
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const endDate = activeSubscription
		? new Intl.DateTimeFormat(locale, {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
		  }).format(new Date(activeSubscription?.endDate))
		: 'unknown';

	if (screen === 'main') {
		return (
			<MainModal
				onClose={onClose}
				onFeedback={() => setScreen('feedback')}
				onCancel={() => setScreen('reasons')}
				endDate={endDate}
			/>
		);
	}

	if (screen === 'feedback') {
		return <Feedback onClose={openMainScreen} open={true} />;
	}

	if (screen === 'reasons') {
		return (
			<ReasonsModal
				onClose={openMainScreen}
				onSubmit={(val) => {
					setReasons(val);
					setScreen('confirm');
				}}
			/>
		);
	}

	if (screen === 'confirm') {
		return (
			<ConfirmModal
				onClose={openMainScreen}
				onConfirm={() => setScreen('prompt')}
			/>
		);
	}

	if (screen === 'prompt') {
		return (
			<PromptModal
				onClose={openMainScreen}
				onConfirm={() => setScreen('canceled')}
				reasons={reasons}
				endDate={endDate}
			/>
		);
	}

	if (screen === 'canceled') {
		return <SuccessModal onClose={onClose} endDate={endDate} />;
	}

	return <FailedOrchestration screen={screen} onClose={onClose} />;
};

const FailedOrchestration = ({
	onClose,
	screen,
}: {
	onClose: () => void;
	screen: Screen;
}) => {
	useEffect(() => {
		DatadogLogger.error(
			LoggerServices.MySubscriptions,
			LoggerMessages.OrchestrationError,
			{
				screen,
			}
		);
		Toaster.showError({ textKey: 'generalErrorMessage' });
		onClose();
	}, [onClose, screen]);

	return null;
};
